import React, { useCallback, useContext, useMemo, useState } from "react";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { MdLink } from "react-icons/md";
import Table from "../../UI/Table/Table";
import { PaginationType } from "../../Types/APITypes";
import Alert from "../../UI/Alert/Alert";
import { NotifyContext } from "../../context/notifyContext";
import { Link } from "react-router-dom";
import AssociationRequestAPI from "../../services/api/AssociationRequestService";
import { BsEyeFill } from "react-icons/bs";

const STATUS_LABEL: Record<string, string> = {
  pending: "En attente",
  accepted: "Acceptée",
  rejected: "Rejetée",
};

const TYPE_LABEL: Record<string, string> = {
  parent_student: "Parent – Élève",
  tutor_student: "Tuteur – Élève",
};

export default function AssociationRequestList() {
  useBreadcrumbs([
    { label: "Demandes d'association", icon: <MdLink />, is_link: true, href: "/app/association-requests" },
    { label: "Liste", is_link: false },
  ]);

  const notify = useContext(NotifyContext);
  const [error, setError] = useState("");
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableInfos, setTableInfos] = useState({ pageCount: 0, total: 0 });

  const fetchData = useCallback(
    (pagination?: PaginationType) => {
      setLoading(true);
      AssociationRequestAPI.get_requests({ pagination })
        .then((response: any) => response?.data)
        .then((res: any) => {
          setData(res?.requests || []);
          setTableInfos({
            pageCount: res?.last_page || 1,
            total: res?.total || 0,
          });
          setError("");
        })
        .catch((err: any) => {
          setError(err?.message || err || "Erreur");
          notify(err?.message || err, "error");
        })
        .finally(() => setLoading(false));
    },
    [notify]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Date",
        accessor: "created_at" as const,
        Cell: (params: any) => {
          const row = params.row?.original;
          return (
            <p>
              {row?.created_at
                ? new Date(row.created_at).toISOString().replace("T", " ").split(".")[0]
                : "-"}
            </p>
          );
        },
      },
      {
        Header: "Type",
        accessor: "type" as const,
        Cell: (params: any) => {
          const row = params.row?.original;
          return <span>{TYPE_LABEL[row?.type] || row?.type}</span>;
        },
      },
      {
        Header: "Élève",
        accessor: "student_id" as const,
        Cell: (params: any) => {
          const row = params.row?.original;
          const student = row?.student;
          if (!student) return <span>-</span>;
          return (
            <span>
              {student.first_name} {student.last_name}
            </span>
          );
        },
      },
      {
        Header: "Parent / Tuteur",
        accessor: "target_parent_id" as const,
        Cell: (params: any) => {
          const row = params.row?.original;
          const parent = row?.target_parent_id ? (row?.targetParent ?? row?.target_parent) : null;
          const tutor = row?.target_tutor_id ? (row?.targetTutor ?? row?.target_tutor) : null;
          const person = parent || tutor;
          if (!person) return <span>-</span>;
          return (
            <span>
              {person.first_name} {person.last_name}
            </span>
          );
        },
      },
      {
        Header: "Statut",
        accessor: "status" as const,
        Cell: (params: any) => {
          const row = params.row?.original;
          const status = row?.status || "pending";
          const color =
            status === "accepted"
              ? "text-green-700 bg-green-100"
              : status === "rejected"
              ? "text-red-700 bg-red-100"
              : "text-amber-700 bg-amber-100";
          return (
            <span className={`inline-flex px-2 py-0.5 rounded text-sm font-medium ${color}`}>
              {STATUS_LABEL[status] || status}
            </span>
          );
        },
      },
      {
        Header: "Action",
        accessor: "id" as const,
        Cell: (params: any) => {
          const row = params.row?.original;
          return (
            <Link
              className="text-primary-900 flex items-center gap-2"
              to={`/app/association-requests/${row?.id}/view`}
            >
              <BsEyeFill /> Voir
            </Link>
          );
        },
      },
    ],
    []
  );

  React.useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="flex flex-col overflow-auto">
      <div className="flex flex-col flex-1">
        {error && <Alert color="error">{error}</Alert>}
        {loading && tableInfos.total === 0 && (
          <div className="flex justify-center w-full p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        )}
        {tableInfos.total === 0 && !loading && (
          <div className="flex flex-col items-center justify-center gap-4 p-8 border border-dashed rounded border-black/60">
            <p>Aucune demande d'association</p>
          </div>
        )}
        <div className="flex flex-1">
          <Table
            columns={columns}
            data={data}
            fetchData={fetchData}
            loading={loading}
            pageCount={tableInfos.pageCount}
            total={tableInfos.total}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
}
